// ============================================================
// BNZ Solutions — Content Data
// All list content lives here so sections stay clean & modular.
// Replace image URLs freely; layouts won't change.
// ============================================================

import {
  Sparkles, Layers, Building2, Armchair, LayoutGrid, Square, Type, Lightbulb,
  Printer, Eye, Wallpaper, PaintBucket, FileImage, Car, Sun, SignpostBig,
} from "lucide-react";

export const stats = [
  { value: "10+", label: "Years of Craft" },
  { value: "1200+", label: "Projects Delivered" },
  { value: "350+", label: "Corporate Clients" },
  { value: "16+", label: "Signage & Branding Services" },
];

export const services = [
  { icon: Sparkles, title: "Glass Stickering", desc: "Precision-cut glass graphics that add privacy, identity and polish to any workspace." },
  { icon: Layers, title: "Frosted Films", desc: "Elegant frosted film for partitions, cabins and facades with a refined finish." },
  { icon: Building2, title: "Office Branding", desc: "End-to-end interior branding that turns workspaces into brand experiences." },
  { icon: Armchair, title: "Reception Branding", desc: "Statement reception walls and 3D logos that impress from the first step." },
  { icon: LayoutGrid, title: "ACP Sign Boards", desc: "Durable aluminium composite boards engineered for premium exteriors." },
  { icon: Square, title: "Acrylic Sign Boards", desc: "High-clarity acrylic signage with backlit and matte options." },
  { icon: Type, title: "Acrylic Letters", desc: "Crisp dimensional lettering for a sharp, corporate presence." },
  { icon: Lightbulb, title: "LED Sign Boards", desc: "Illuminated signage built for visibility, day and night." },
  { icon: Printer, title: "Vinyl Printing", desc: "Vivid, weather-resistant vinyl prints for interiors and exteriors." },
  { icon: Eye, title: "One Way Vision Stickers", desc: "See-out privacy graphics ideal for glass facades and vehicles." },
  { icon: Wallpaper, title: "Wallpaper Installation", desc: "Premium wallcoverings installed with a flawless seamless finish." },
  { icon: PaintBucket, title: "Wall Graphics", desc: "Custom wall art and murals that carry your brand story." },
  { icon: FileImage, title: "Digital Printing", desc: "High-resolution large-format and commercial digital printing." },
  { icon: Car, title: "Vehicle Branding", desc: "Turn fleets into moving billboards with durable wraps." },
  { icon: Sun, title: "Sun Control Films", desc: "Heat and glare reduction films for comfort and efficiency." },
  { icon: SignpostBig, title: "Corporate Signage", desc: "Wayfinding and identity signage systems for enterprises." },
];

export const whyUs = [
  { title: "Premium Craftsmanship", desc: "Meticulous finishing standards on every install, down to the last edge." },
  { title: "End-to-End Delivery", desc: "Design, fabrication and installation managed under one accountable team." },
  { title: "On-Time, On-Site", desc: "Reliable timelines with minimal disruption to your working hours." },
  { title: "Enterprise-Grade Materials", desc: "Durable, certified materials sourced for longevity and finish." },
  { title: "Local Bengaluru Expertise", desc: "Deep experience serving corporates across Bengaluru & Karnataka." },
  { title: "Single Point of Contact", desc: "One dedicated project lead from brief to handover." },
];

export const industries = [
  "Corporate Offices", "IT & Tech Parks", "Co-working Spaces", "Retail & Showrooms",
  "Hospitality & Hotels", "Healthcare & Clinics", "Real Estate & Builders",
  "Educational Institutes", "Banks & Finance", "Manufacturing & Warehousing",
  "Restaurants & Cafés", "Automobile Showrooms",
];

export const process = [
  { step: "01", title: "Consultation", desc: "We understand your space, brand and objectives on a detailed site visit." },
  { step: "02", title: "Design & Proposal", desc: "Concepts, material recommendations and a transparent quotation." },
  { step: "03", title: "Fabrication", desc: "In-house production with strict quality checks at every stage." },
  { step: "04", title: "Installation", desc: "Clean, precise on-site installation by our expert crew." },
  { step: "05", title: "Handover & Support", desc: "Final walkthrough and ongoing maintenance support." },
];

export const testimonials = [
  { name: "Rohan Mehta", role: "Facilities Head, IT Firm — Whitefield", quote: "BNZ transformed our three floors into a branded environment. The frosted film and reception wall are flawless." },
  { name: "Ananya Rao", role: "Founder, Co-working Space — Koramangala", quote: "Professional, punctual and premium. Their signage instantly elevated how clients perceive our space." },
  { name: "Vikram Shetty", role: "Retail Operations — MG Road", quote: "From ACP boards to LED signage, the finish quality is easily the best we've seen in Bengaluru." },
  { name: "Priya Nair", role: "Admin Manager, Healthcare Group", quote: "Seamless project management and zero disruption to our clinic hours. Highly recommended." },
];

export const faqs = [
  { q: "Which areas in Bengaluru do you serve?", a: "We deliver office branding, signage and printing across all of Bengaluru (Bangalore) and greater Karnataka, including Whitefield, Koramangala, Electronic City, MG Road and surrounding business hubs." },
  { q: "Do you handle design as well as installation?", a: "Yes. BNZ Solutions is an end-to-end partner — we manage design, in-house fabrication and on-site installation under one accountable team." },
  { q: "How long does a typical office branding project take?", a: "Timelines depend on scope, but most standard signage and branding projects are completed within 3–10 working days after design approval." },
  { q: "What materials do you use for sign boards?", a: "We use enterprise-grade ACP, acrylic, LED modules, vinyl and certified films selected for durability, finish and longevity." },
  { q: "Can you brand our workspace with minimal disruption?", a: "Absolutely. We schedule installations around your working hours and offer after-hours execution to keep operations running." },
  { q: "How do I get a quote?", a: "Send your requirement via the enquiry form, WhatsApp or a direct call. We'll arrange a site visit and share a transparent quotation." },
];

// Portfolio — replace `img` URLs anytime; grid layout stays intact.
const IMG = {
  lobby: "https://images.unsplash.com/photo-1758448656990-398443036eb4",
  reception: "https://images.unsplash.com/photo-1758448093806-88b2089068ab",
  glass: "https://images.unsplash.com/photo-1770992161068-cff084f5ad04",
  led: "https://images.unsplash.com/photo-1661298889141-14da83db9821",
};
const q = "?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

export const portfolioCategories = [
  "All", "Office Branding", "Reception", "Glass & Frosted", "Signage", "LED",
];

export const portfolio = [
  { title: "Corporate HQ Branding", category: "Office Branding", img: IMG.lobby + q },
  { title: "Reception Feature Wall", category: "Reception", img: IMG.reception + q },
  { title: "Frosted Glass Partitions", category: "Glass & Frosted", img: IMG.glass + q },
  { title: "Illuminated LED Signage", category: "LED", img: IMG.led + q },
  { title: "Executive Cabin Branding", category: "Office Branding", img: IMG.lobby + "?crop=faces&cs=srgb&fm=jpg&q=80&w=800" },
  { title: "Glass Stickering — Meeting Rooms", category: "Glass & Frosted", img: IMG.glass + "?crop=edges&cs=srgb&fm=jpg&q=80&w=800" },
  { title: "ACP Exterior Sign Board", category: "Signage", img: IMG.reception + "?crop=edges&cs=srgb&fm=jpg&q=80&w=800" },
  { title: "Storefront LED Display", category: "LED", img: IMG.led + "?crop=entropy&cs=srgb&fm=jpg&q=80&w=800" },
  { title: "Lobby Identity Signage", category: "Signage", img: IMG.lobby + "?crop=edges&cs=srgb&fm=jpg&q=80&w=800" },
];

export const heroImage = IMG.lobby + "?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
export const aboutImage = IMG.reception + "?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
